import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { DataTable, type DataColumn } from '@/components/data-table'
import { BtnGhost, BtnPrimary, Field, TextInput } from '@/components/field'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate, downloadCsv } from '@/lib/api'
import { fmtDate } from '@/lib/labels'

const PAGE_SIZE = 25

type CustomerRow = {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string | null
  status?: string
  source?: string
  emailVerifiedAt: string | null
  createdAt: string
  orderCount: number
}

function waDigits(phone: string) {
  return phone.replace(/\D/g, '')
}

function urlQ() {
  return new URLSearchParams(window.location.search).get('q')?.trim() ?? ''
}

export function Clients() {
  const toast = useToast()
  const qc = useQueryClient()
  const searchQ = urlQ()
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('')

  const customers = useQuery({
    queryKey: ['admin-customers', searchQ, page],
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('limit', String(PAGE_SIZE))
      params.set('page', String(page))
      if (searchQ) params.set('q', searchQ)
      return api<{ items: CustomerRow[]; total: number }>(
        `/admin/customers?${params.toString()}`,
      )
    },
  })

  const create = useMutation({
    mutationFn: () =>
      apiMutate<{ id: string }>('/admin/customers', 'POST', {
        email,
        firstName,
        lastName: lastName || undefined,
        phone: phone || undefined,
      }),
    onSuccess: (c) => {
      toast.ok('Cliente créée')
      setCreateOpen(false)
      setEmail('')
      setFirstName('')
      setLastName('')
      setPhone('')
      void qc.invalidateQueries({ queryKey: ['admin-customers'] })
      window.location.href = `/clients/${c.id}`
    },
    onError: (e: Error) => toast.err(e.message),
  })

  const items = customers.data?.items ?? []
  const total = customers.data?.total ?? items.length

  const columns = useMemo<DataColumn<CustomerRow>[]>(
    () => [
      {
        header: 'Nom',
        id: 'name',
        cell: (c) => (
          <span className="font-medium text-encre-admin">
            {c.firstName} {c.lastName}
          </span>
        ),
      },
      { header: 'E-mail', accessorKey: 'email' },
      {
        header: 'Téléphone',
        accessorKey: 'phone',
        cell: (c) => <span className="text-second-admin">{c.phone ?? '—'}</span>,
      },
      {
        header: 'Statut',
        accessorKey: 'status',
        cell: (c) => (
          <StatusPill
            label={c.status === 'DISABLED' ? 'Désactivé' : 'Actif'}
            tone={c.status === 'DISABLED' ? 'bad' : 'ok'}
          />
        ),
      },
      {
        header: 'Commandes',
        accessorKey: 'orderCount',
        cell: (c) => <span className="tabular-nums">{c.orderCount}</span>,
      },
      {
        header: 'Vérifié',
        id: 'verified',
        cell: (c) => (
          <StatusPill
            label={c.emailVerifiedAt ? 'Oui' : 'Non'}
            tone={c.emailVerifiedAt ? 'ok' : 'warn'}
          />
        ),
      },
      {
        header: 'Depuis',
        accessorKey: 'createdAt',
        cell: (c) => <span className="text-second-admin">{fmtDate(c.createdAt)}</span>,
      },
      {
        header: '',
        id: 'actions',
        cell: (c) => {
          const digits = c.phone ? waDigits(c.phone) : ''
          return (
            <div className="text-right" onClick={(e) => e.stopPropagation()}>
              <RowActions
                items={[
                  {
                    label: 'Ouvrir',
                    onSelect: () => {
                      window.location.href = `/clients/${c.id}`
                    },
                  },
                  ...(digits
                    ? [
                        {
                          label: 'WhatsApp',
                          onSelect: () => {
                            window.open(`https://wa.me/${digits}`, '_blank')
                          },
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )
        },
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {searchQ ? (
          <p className="text-sm text-second-admin">
            Recherche : <span className="text-encre-admin">{searchQ}</span>
          </p>
        ) : (
          <span />
        )}
        <div className="flex flex-wrap gap-2">
          <BtnGhost
            onClick={() =>
              void downloadCsv('/admin/exports/customers.csv', 'customers.csv').catch(
                (e: Error) => toast.err(e.message),
              )
            }
          >
            Export CSV
          </BtnGhost>
          <BtnPrimary onClick={() => setCreateOpen(true)}>Nouvelle cliente</BtnPrimary>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        getRowId={(c) => c.id}
        isLoading={customers.isLoading}
        isError={customers.isError}
        errorMessage="Impossible de charger les clients."
        emptyTitle="Aucun client"
        emptyBody="Les comptes créés sur la boutique ou en back-office apparaîtront ici."
        onRowClick={(c) => {
          window.location.href = `/clients/${c.id}`
        }}
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          onPageChange: setPage,
        }}
      />

      <Modal
        open={createOpen}
        title="Nouvelle cliente"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <BtnGhost onClick={() => setCreateOpen(false)}>Annuler</BtnGhost>
            <BtnPrimary
              disabled={create.isPending || !email.trim() || !firstName.trim()}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Création…' : 'Créer'}
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Prénom">
            <TextInput value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          </Field>
          <Field label="Nom">
            <TextInput value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
          <Field label="E-mail">
            <TextInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>
          <Field label="Téléphone" hint="Format RDC, ex. 0812345678">
            <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}
